import { Bookmark, Plus, X } from 'lucide-react';
import { useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { TableViewRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { errorMessage } from '../../../lib/api-client';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { useDeleteTableView, useSaveTableView, useTableViews, useUpdateTableView } from '../../../lib/table-views-api';
import { useFocusOnOpen } from '../../../lib/use-focus-on-open';
import { useToasts } from '../../../stores/toasts';
import { isSameView } from '../ranking-views';
import styles from './ranking.module.css';

// F23.10 (ADR 0049, DECISIONS-LOG 2026-10-17 "Ranking 2.0 Part 1" Q1): this job's saved views, above the
// toolbar — the viewer's own and the ones colleagues shared — each with how many it shows. One click opens
// one; "edited" says the screen has moved on from it. Save view is a pop-over anchored to its button.

const SHARE_OPTIONS = [
  { value: 'private' as const, label: 'Only me' },
  { value: 'shared' as const, label: 'Everyone on this job' },
];

interface SavedViewsBarProps {
  readonly jobId: string;
  /** The screen as it stands, as a view would save it. */
  readonly current: Readonly<Record<string, string>>;
  /** The view last opened, from the address. */
  readonly appliedViewId: string | undefined;
  /** What the screen would save, in words, for the pop-over. */
  readonly summary: readonly string[];
  readonly countOf: (state: Readonly<Record<string, string>>) => number | null;
  readonly onApply: (view: TableViewRow) => void;
  readonly onSaved: (view: TableViewRow) => void;
  /** Where the Save view pop-over is open: by this bar's button, or by the summary line's "Save as view". */
  readonly saveAnchor: AnchorRect | null;
  readonly onSaveOpen: (anchor: AnchorRect) => void;
  readonly onSaveClose: () => void;
}

export function SavedViewsBar({ jobId, current, appliedViewId, summary, countOf, onApply, onSaved, saveAnchor, onSaveOpen, onSaveClose }: SavedViewsBarProps): ReactNode {
  const views = useTableViews('ranking', true, jobId);
  const save = useSaveTableView('ranking', jobId);
  const update = useUpdateTableView('ranking', jobId);
  const remove = useDeleteTableView('ranking', jobId);
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const rows = views.data ?? [];
  const applied = rows.find((view) => view.id === appliedViewId);
  const isAppliedEdited = applied !== undefined && !isSameView(applied.state.filters, current);

  function removeView(view: TableViewRow): void {
    remove.mutate(view, {
      // Undo beats confirm (§5a rule 8): the view comes back as it was, under the same name.
      onSuccess: () => showUndo(`View "${view.name}" deleted`, () => save.mutate({ screen: 'ranking', name: view.name, isShared: view.isShared, state: view.state })),
      onError: (cause) => show({ tone: 'danger', message: "The view wasn't deleted", detail: errorMessage(cause) }),
    });
  }

  function saveChanges(view: TableViewRow): void {
    update.mutate(
      { id: view.id, input: { name: view.name, isShared: view.isShared, state: { filters: { ...current }, hiddenColumns: [...view.state.hiddenColumns] }, version: view.version } },
      {
        onSuccess: (saved) => {
          show({ tone: 'success', message: `View "${saved.name}" updated` });
          onSaved(saved);
          onSaveClose();
        },
        onError: (cause) => show({ tone: 'danger', message: "The view wasn't updated", detail: errorMessage(cause) }),
      },
    );
  }

  return (
    <div className={styles.viewsBar}>
      <span className={styles.eyebrow}>Saved views</span>
      {rows.map((view) => {
        const isOn = isSameView(view.state.filters, current);
        const count = countOf(view.state.filters);
        return (
          <span key={view.id} className={styles.viewPill} data-on={isOn || undefined}>
            <button type="button" className={styles.viewOpen} aria-pressed={isOn} onClick={() => onApply(view)}>
              <span>{view.name}</span>
              {count !== null && <span className={styles.viewCount}>{count.toLocaleString('en-US')}</span>}
              <span className={styles.viewScope}>{scopeOf(view)}</span>
              {view.id === appliedViewId && isAppliedEdited && <span className={styles.viewEdited}>· edited</span>}
            </button>
            {view.isMine && (
              <button type="button" className={styles.viewRemove} aria-label={`Delete the view ${view.name}`} onClick={() => removeView(view)}>
                <X size={12} aria-hidden />
              </button>
            )}
          </span>
        );
      })}
      <Button size="sm" variant="ghost" icon={rows.length === 0 ? Bookmark : Plus} onClick={(event) => onSaveOpen(anchorFrom(event.currentTarget))}>
        Save view
      </Button>
      {rows.length === 0 && <span className={styles.viewsHint}>Save what you're looking at, to come back to it in one click.</span>}
      <Popover anchor={saveAnchor} onClose={onSaveClose} label="Save this view" align="left">
        {saveAnchor && (
          <SaveViewForm
            summary={summary}
            changesTo={applied?.isMine && isAppliedEdited ? applied : undefined}
            isSaving={save.isPending || update.isPending}
            onSaveChanges={saveChanges}
            onCancel={onSaveClose}
            onSave={async (name, isShared) => {
              try {
                const saved = await save.mutateAsync({ screen: 'ranking', name, isShared, state: { filters: { ...current }, hiddenColumns: [] } });
                show({ tone: 'success', message: `View "${saved.name}" saved`, detail: isShared ? 'Everyone on this job can use it.' : 'Only you can see it.' });
                onSaved(saved);
                onSaveClose();
                return null;
              } catch (cause) {
                return errorMessage(cause);
              }
            }}
          />
        )}
      </Popover>
    </div>
  );
}

function scopeOf(view: TableViewRow): string {
  if (!view.isShared) return 'Only me';
  return view.isMine ? 'Team' : (view.ownerName ?? 'Team');
}

interface SaveViewFormProps {
  readonly summary: readonly string[];
  readonly changesTo: TableViewRow | undefined;
  readonly isSaving: boolean;
  readonly onSaveChanges: (view: TableViewRow) => void;
  /** Resolves to an error message, or null when saved. */
  readonly onSave: (name: string, isShared: boolean) => Promise<string | null>;
  readonly onCancel: () => void;
}

function SaveViewForm({ summary, changesTo, isSaving, onSaveChanges, onSave, onCancel }: SaveViewFormProps): ReactNode {
  const nameId = useId();
  const nameRef = useRef<HTMLInputElement | null>(null);
  useFocusOnOpen(nameRef);
  const [name, setName] = useState('');
  const [share, setShare] = useState<'private' | 'shared'>('shared');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (name.trim() === '') {
      setError('Give the view a name.');
      return;
    }
    setError(await onSave(name.trim(), share === 'shared'));
  }

  return (
    <form className={styles.saveView} onSubmit={(event) => void submit(event)}>
      <h2 className={styles.saveViewTitle}>Save this view</h2>
      {changesTo && (
        <Button variant="outline" onClick={() => onSaveChanges(changesTo)} disabled={isSaving}>
          Save changes to "{changesTo.name}"
        </Button>
      )}
      <div className={styles.saveViewField}>
        <label htmlFor={nameId} className={styles.saveViewLabel}>
          {changesTo ? 'Or save as a new view' : 'Name'}
        </label>
        <Input id={nameId} ref={nameRef} value={name} maxLength={60} placeholder="e.g. Tally + VAT, Dhaka" isInvalid={error !== null} onChange={(event) => setName(event.target.value)} />
        {error && <p className={styles.saveViewError}>{error}</p>}
      </div>
      <div className={styles.saveViewField}>
        <span className={styles.saveViewLabel}>It keeps</span>
        <ul className={styles.saveViewKeeps}>
          {summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
      <Segmented label="Who sees it" value={share} options={SHARE_OPTIONS} onChange={setShare} />
      <p className={styles.saveViewNote}>Opening a view never changes anyone's score.</p>
      <div className={styles.saveViewActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="solid" disabled={isSaving}>
          Save view
        </Button>
      </div>
    </form>
  );
}
